/**
 * Wallet API Client for lukhas.id
 *
 * Connects to the lukhas.team wallet API for Apple Wallet
 * PassKit integration and QRG (JWS-signed QR) generation.
 *
 * @module @lukhas.id/lib/wallet-client
 */

/** Wallet API configuration */
const WALLET_API_URL = import.meta.env.VITE_WALLET_API_URL || 'https://api.lukhas.team';

/**
 * Response from pass issuance API
 */
export interface PassIssueResponse {
  ok: boolean;
  serialNumber?: string;
  authToken?: string;
  saveUrl?: string;
  expiresAt?: string;
  error?: string;
}

/**
 * Options for issuing a wallet pass
 */
export interface PassIssueOptions {
  deviceId?: string;
  action?: string;
  expiresInHours?: number;
  platform?: 'apple' | 'google';
}

/**
 * Issue an Apple Wallet or Google Wallet pass
 *
 * For Apple Wallet, this downloads a .pkpass file directly.
 * For Google Wallet, this returns a save URL.
 *
 * @param options - Pass issuance options
 * @returns For Apple: initiates download. For Google: returns save URL.
 */
export async function issueWalletPass(
  options: PassIssueOptions = {}
): Promise<PassIssueResponse> {
  const { platform = 'apple', deviceId, action, expiresInHours } = options;

  // Get auth token from storage
  const authToken = localStorage.getItem('auth_token');
  if (!authToken) {
    return { ok: false, error: 'Not authenticated' };
  }

  try {
    const response = await fetch(`${WALLET_API_URL}/api/wallet/pass/issue`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        ...(deviceId && { 'X-Device-Id': deviceId }),
      },
      body: JSON.stringify({
        platform,
        action: action || 'wallet.bind',
        expiresInHours: expiresInHours || 24,
        deviceId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: errorData.error || `Request failed with status ${response.status}`,
      };
    }

    // For Google Wallet, return JSON response with save URL
    if (platform === 'google') {
      const data = await response.json();
      return {
        ok: true,
        serialNumber: data.serialNumber,
        authToken: data.authToken,
        saveUrl: data.saveUrl,
        expiresAt: data.expiresAt,
      };
    }

    // For Apple Wallet, trigger download of .pkpass file
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'lukhas-id.pkpass';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    // Extract headers for response
    const serialNumber = response.headers.get('X-Wallet-Serial-Number') || undefined;
    const walletAuthToken = response.headers.get('X-Wallet-Auth-Token') || undefined;

    return {
      ok: true,
      serialNumber,
      authToken: walletAuthToken,
    };
  } catch (error) {
    console.error('[WalletClient] Pass issuance failed:', error);
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Generate a QRG (JWS-signed QR) for out-of-band authentication
 *
 * @returns QRG URL or auth request ID
 */
export async function generateQRG(): Promise<string> {
  const authToken = localStorage.getItem('auth_token');
  if (!authToken) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${WALLET_API_URL}/api/qrg/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'QRG generation failed');
  }

  const data = await response.json();
  return data.qrgUrl || data.authRequestId;
}

/**
 * Check if a wallet pass is currently active for this device
 *
 * @returns True if user has an active wallet pass
 */
export async function hasActivePass(): Promise<boolean> {
  const authToken = localStorage.getItem('auth_token');
  if (!authToken) {
    return false;
  }

  try {
    const response = await fetch(`${WALLET_API_URL}/api/wallet/pass/status`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.hasActivePass === true;
  } catch {
    return false;
  }
}

/**
 * Revoke all wallet passes for the current user
 *
 * @returns True if revocation succeeded
 */
export async function revokeAllPasses(): Promise<boolean> {
  const authToken = localStorage.getItem('auth_token');
  if (!authToken) {
    return false;
  }

  try {
    const response = await fetch(`${WALLET_API_URL}/api/wallet/pass/revoke`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });

    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Get stable device ID for pass binding
 * Uses fingerprinting + localStorage for consistency
 */
export function getDeviceId(): string {
  const storageKey = 'lukhas_device_id';
  let deviceId = localStorage.getItem(storageKey);

  if (!deviceId) {
    // Generate a new device ID based on fingerprint + random component
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width,
      screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
    ].join('|');

    // Simple hash of fingerprint
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    // Combine with random component for uniqueness
    const random = Math.random().toString(36).substring(2, 10);
    deviceId = `dev_${Math.abs(hash).toString(36)}_${random}`;

    localStorage.setItem(storageKey, deviceId);
  }

  return deviceId;
}
